'use client';

import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';

export type BreadcrumbLevel = {
	level: number;
	data: {
		name: string;
		href?: string;
	};
};

type BreadcrumbContextType = {
	register: (_level: number, _data: BreadcrumbLevel['data']) => void;
	unregister: (_level: number) => void;
	breadcrumbs: Array<{ label: string; href?: string }>;
};

export const BreadcrumbContext = createContext<BreadcrumbContextType | null>(null);

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
	const [levels, setLevels] = useState<BreadcrumbLevel[]>([]);
	const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
	const previousPathname = useRef(pathname);
	const previousLevels = useRef(levels);

	useEffect(() => {
		if (previousPathname.current === pathname && previousLevels.current === levels) {
			return;
		}

		previousPathname.current = pathname;
		previousLevels.current = levels;
	}, [levels, pathname]);

	const register = useCallback((level: number, data: BreadcrumbLevel['data']) => {
		setLevels((prev) => {
			const filtered = prev.filter((l) => l.level !== level);
			const newLevels = [...filtered, { level, data }];
			return JSON.stringify(newLevels) === JSON.stringify(prev) ? prev : newLevels;
		});
	}, []);

	const unregister = useCallback((level: number) => {
		setLevels((prev) => {
			const filtered = prev.filter((l) => l.level !== level);
			return filtered.length === prev.length ? prev : filtered;
		});
	}, []);

	const breadcrumbs = levels
		.sort((a, b) => a.level - b.level)
		.map((level, index) => ({
			label: level.data.name,
			href: index === levels.length - 1 ? undefined : level.data.href,
		}));

	return (
		<BreadcrumbContext.Provider value={{ register, unregister, breadcrumbs }}>{children}</BreadcrumbContext.Provider>
	);
}
